import React, { useEffect, useId, useMemo, useState } from 'react';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useIsNarrowScreen } from '@/hooks/useMediaQuery';
import { replaceInternalOwnerEmails } from '@/utils/internalOwnerLabel';
import UserNotificationsBell from './UserNotificationsBell';
import { ControlBar } from '@/ui/components';
import type { ControlBarAction, ControlBarOption } from '@/ui/components';
import './PageHeader.css';

export interface PageHeaderProps {
  title: string;
  itemCount: number;
  subtitle?: string;
  gradientColor?: string;
  icon?: string;
  searchEnabled?: boolean;
  searchQuery?: string;
  searchPlaceholder?: string;
  onSearchChange?: (query: string) => void;
  searchActions?: React.ReactNode;
  sortEnabled?: boolean;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  sortOptions?: ControlBarOption[];
  onSortChange?: (sortBy: string) => void;
  onSortDirectionToggle?: () => void;
  selectEnabled?: boolean;
  selectedCount?: number;
  totalCount?: number;
  onSelectAll?: (checked: boolean) => void;
  filters?: React.ReactNode;
  userLabel?: string;
  roleLabel?: string;
  actions?: ControlBarAction[];
  mobileHeaderAction?: React.ReactNode;
  density?: 'compact' | 'default' | 'comfortable';
  controlBarDensity?: 'default' | 'compact';
}

const baseFontFamily = 'var(--font-family)';

function truncateSummaryValue(value: string, maxLength = 24) {
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }

  return `${trimmed.slice(0, maxLength - 3)}...`;
}

function joinMobileControlSummary(parts: string[]) {
  const visibleParts = parts.slice(0, 2);
  const overflowCount = Math.max(0, parts.length - visibleParts.length);
  return overflowCount > 0
    ? `${visibleParts.join(' / ')} / +${overflowCount}`
    : visibleParts.join(' / ');
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  itemCount,
  subtitle,
  gradientColor = 'linear-gradient(135deg, #173B84 0%, #2251B4 52%, #4B6FBC 100%)',
  icon,
  searchEnabled = true,
  searchQuery = '',
  searchPlaceholder = 'Search...',
  onSearchChange,
  searchActions,
  sortEnabled = true,
  sortBy = 'name',
  sortDirection = 'asc',
  sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'date', label: 'Date' },
  ],
  onSortChange,
  onSortDirectionToggle,
  selectEnabled = false,
  selectedCount = 0,
  totalCount = 0,
  onSelectAll,
  filters,
  userLabel,
  roleLabel,
  actions = [],
  mobileHeaderAction,
  density = 'compact',
  controlBarDensity = 'compact',
}) => {
  const navigate = useNavigate();
  const isNarrowLayout = useIsNarrowScreen(640);
  const controlsPanelId = useId();
  const [mobileControlsOpen, setMobileControlsOpen] = useState(false);
  const normalizedUserLabel = replaceInternalOwnerEmails(userLabel);
  const densityConfig = {
    compact: {
      horizontalPadding: isNarrowLayout ? '12px' : '14px',
      topPadding: isNarrowLayout ? '8px' : '8px',
      bottomPadding: isNarrowLayout ? '10px' : '8px',
      minHeight: '48px',
      titleSize: isNarrowLayout ? '21px' : '18px',
      subtitleSize: '12px',
      metaSize: '10px',
      homePadding: isNarrowLayout ? '8px 9px' : '7px 10px',
    },
    default: {
      horizontalPadding: isNarrowLayout ? '12px' : '14px',
      topPadding: isNarrowLayout ? '10px' : '10px',
      bottomPadding: isNarrowLayout ? '12px' : '10px',
      minHeight: '52px',
      titleSize: isNarrowLayout ? '22px' : '20px',
      subtitleSize: '12px',
      metaSize: '11px',
      homePadding: '6px 10px',
    },
    comfortable: {
      horizontalPadding: isNarrowLayout ? '12px' : '16px',
      topPadding: isNarrowLayout ? '12px' : '12px',
      bottomPadding: isNarrowLayout ? '14px' : '12px',
      minHeight: '48px',
      titleSize: isNarrowLayout ? '24px' : '22px',
      subtitleSize: '13px',
      metaSize: '12px',
      homePadding: '7px 12px',
    },
  }[density];
  const hasSearchControls = Boolean(searchEnabled);
  const hasSortControls = Boolean(sortEnabled && sortOptions.length > 0);
  const hasSelectionControls = Boolean(selectEnabled);
  const hasFilterControls = Boolean(filters);
  const hasFinderControls = hasSearchControls || hasSortControls || hasSelectionControls || hasFilterControls;
  const hasActionControls = actions.length > 0 || Boolean(searchActions);
  const hasAnyControls = hasFinderControls || hasActionControls;
  const trimmedSearchQuery = searchQuery.trim();
  const activeSortLabel = sortOptions.find((option) => option.value === sortBy)?.label || sortBy;
  const mobileControlsLabel = hasFilterControls
    ? (hasSearchControls || hasSortControls ? 'Search & filters' : 'Filters')
    : hasSearchControls && hasSortControls
      ? 'Search & sort'
      : hasSearchControls
        ? 'Search'
        : hasSortControls
          ? 'Sort'
          : hasSelectionControls
            ? 'Selection'
            : 'Controls';
  const mobileControlsMeta = useMemo(() => {
    const parts = [
      selectedCount > 0 ? `${selectedCount} selected` : null,
      trimmedSearchQuery ? `Search: ${truncateSummaryValue(trimmedSearchQuery)}` : null,
      hasSortControls && activeSortLabel ? `Sort: ${activeSortLabel} ${sortDirection}` : null,
      hasFilterControls ? 'Filters' : null,
    ].filter((part): part is string => Boolean(part));

    return joinMobileControlSummary(parts);
  }, [activeSortLabel, hasFilterControls, hasSortControls, selectedCount, sortDirection, trimmedSearchQuery]);

  useEffect(() => {
    if (!isNarrowLayout) {
      setMobileControlsOpen(true);
      return;
    }

    setMobileControlsOpen(Boolean(selectEnabled));
  }, [isNarrowLayout, selectEnabled, title]);

  const renderControlBar = ({
    includeFinderControls,
    includeActionControls,
    stacked,
  }: {
    includeFinderControls: boolean;
    includeActionControls: boolean;
    stacked: boolean;
  }) => (
    <ControlBar
      searchEnabled={includeFinderControls && searchEnabled}
      searchQuery={searchQuery}
      searchPlaceholder={searchPlaceholder}
      onSearchChange={onSearchChange}
      searchActions={includeActionControls ? searchActions : undefined}
      sortEnabled={includeFinderControls && sortEnabled}
      sortBy={sortBy}
      sortDirection={sortDirection}
      sortOptions={sortOptions}
      onSortChange={onSortChange}
      onSortDirectionToggle={onSortDirectionToggle}
      selectEnabled={includeFinderControls && selectEnabled}
      selectedCount={selectedCount}
      totalCount={totalCount}
      onSelectAll={onSelectAll}
      filters={includeFinderControls ? filters : undefined}
      actions={includeActionControls ? actions : []}
      stacked={stacked}
      density={controlBarDensity}
    />
  );

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 900,
        backgroundColor: '#ffffff',
        borderBottom: '1px solid rgba(211, 220, 232, 0.78)',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
      }}
    >
      <div
        style={{
          background: '#ffffff',
          color: 'var(--color-text)',
          paddingLeft: densityConfig.horizontalPadding,
          paddingRight: densityConfig.horizontalPadding,
          paddingBottom: densityConfig.bottomPadding,
          paddingTop: isNarrowLayout
            ? `calc(${densityConfig.topPadding} + env(safe-area-inset-top))`
            : densityConfig.topPadding,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'nowrap',
            minHeight: densityConfig.minHeight,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isNarrowLayout ? '8px' : '10px',
              minWidth: 0,
              flex: '1 1 auto',
            }}
          >
            <button
              type="button"
              onClick={() => navigate('/')}
              title="Return to Dashboard"
              style={{
                background: '#f5f8fc',
                border: '1px solid rgba(211, 220, 232, 0.86)',
                borderRadius: '10px',
                color: '#344054',
                cursor: 'pointer',
                padding: densityConfig.homePadding,
                fontSize: '12px',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                whiteSpace: 'nowrap',
                fontFamily: baseFontFamily,
              }}
            >
              <span style={{ fontSize: '14px', lineHeight: 1 }}>←</span>
              <span style={{ display: isNarrowLayout ? 'none' : 'inline' }}>Home</span>
            </button>

            <div style={{ minWidth: 0, flex: '1 1 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
                {icon && <span style={{ fontSize: '16px', lineHeight: 1, color: 'var(--color-primary)' }}>{icon}</span>}
                <h1
                  style={{
                    margin: 0,
                    fontSize: densityConfig.titleSize,
                    fontWeight: 800,
                    lineHeight: 1.15,
                    fontFamily: baseFontFamily,
                    letterSpacing: 0,
                  }}
                >
                  {title}
                </h1>
              </div>
              {subtitle && !isNarrowLayout ? (
                <div
                  style={{
                    marginTop: '3px',
                    fontSize: densityConfig.subtitleSize,
                    lineHeight: 1.35,
                    color: '#667085',
                    fontFamily: baseFontFamily,
                    maxWidth: '100%',
                  }}
                >
                  {subtitle}
                </div>
              ) : null}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '10px',
              flex: '0 0 auto',
              minWidth: 0,
            }}
          >
            {isNarrowLayout && mobileHeaderAction ? (
              <div className="page-header__mobile-header-action">
                {mobileHeaderAction}
              </div>
            ) : null}
            <UserNotificationsBell />
            {!isNarrowLayout ? (
              <div
                style={{
                  textAlign: 'right',
                  fontFamily: baseFontFamily,
                  fontSize: densityConfig.metaSize,
                  lineHeight: 1.35,
                  color: '#667085',
                  minWidth: 0,
                }}
              >
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#10243f' }}>
                  {itemCount} {itemCount === 1 ? 'item' : 'items'}
                </div>
                {(normalizedUserLabel || roleLabel) && (
                  <div style={{ marginTop: '2px' }}>
                    {normalizedUserLabel ? `User: ${normalizedUserLabel}` : ''}
                    {normalizedUserLabel && roleLabel ? ' · ' : ''}
                    {roleLabel ? `Role: ${roleLabel}` : ''}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {hasAnyControls && (
        isNarrowLayout ? (
          <>
            {hasActionControls ? (
              <div className="page-header__mobile-actions">
                {renderControlBar({
                  includeFinderControls: false,
                  includeActionControls: true,
                  stacked: false,
                })}
              </div>
            ) : null}
            {hasFinderControls ? (
              <div className={`page-header-controls${mobileControlsOpen ? ' page-header-controls--open' : ''}`}>
                <button
                  type="button"
                  className="page-header-controls__summary"
                  aria-expanded={mobileControlsOpen}
                  aria-controls={controlsPanelId}
                  onClick={() => setMobileControlsOpen((current) => !current)}
                >
                  <span className="page-header-controls__summary-main">
                    <SlidersHorizontal size={16} strokeWidth={2.2} aria-hidden="true" />
                    <span>{mobileControlsLabel}</span>
                  </span>
                  <span className="page-header-controls__summary-meta">
                    {mobileControlsMeta || `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`}
                  </span>
                  <ChevronDown className="page-header-controls__chevron" size={16} strokeWidth={2.2} aria-hidden="true" />
                </button>
                {mobileControlsOpen ? (
                  <div id={controlsPanelId} className="page-header-controls__panel">
                    {renderControlBar({
                      includeFinderControls: true,
                      includeActionControls: false,
                      stacked: true,
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          renderControlBar({
            includeFinderControls: true,
            includeActionControls: true,
            stacked: false,
          })
        )
      )}
    </div>
  );
};

export default PageHeader;
